import {
  Box,
  Flex,
  FlexProps,
  Heading,
  Icon,
  Progress,
  Text,
} from '@everlywell/ui-kit';
import { Star } from '@phosphor-icons/react';
import React, { useId } from 'react';

export type ReviewCardProps = {
  total: number;
  rating: number;
  histogram: [number, number, number, number, number];
  slots?: ReviewsCardSlotOverrides;
} & FlexProps;

type SlotOverride = {
  sx?: any;
};

export type ReviewsCardSlotOverrides = {
  root?: SlotOverride;
  header?: SlotOverride;
  icon?: SlotOverride;
  heading?: SlotOverride;
  ratingLabel?: SlotOverride;
  histogram?: SlotOverride;
  histogramItem?: SlotOverride;
  histogramLabel?: SlotOverride;
  histogramBarContainer?: SlotOverride;
  histogramBar?: SlotOverride;
  histogramPercentage?: SlotOverride;
};

const mergeSlotSx = (
  ...sxValues: Array<ReviewCardProps['sx'] | Record<string, any> | undefined>
): ReviewCardProps['sx'] => {
  const merged = sxValues.reduce<Record<string, any>>((acc, sxValue) => {
    if (!sxValue) {
      return acc;
    }

    if (Array.isArray(sxValue)) {
      return sxValue.reduce<Record<string, any>>((innerAcc, item) => {
        if (!item || typeof item === 'function') {
          return innerAcc;
        }

        if (Array.isArray(item)) {
          return {
            ...innerAcc,
            ...item.reduce<Record<string, any>>((nestedAcc, nestedItem) => {
              if (!nestedItem || typeof nestedItem === 'function') {
                return nestedAcc;
              }

              return {
                ...nestedAcc,
                ...nestedItem,
              };
            }, {}),
          };
        }

        return {
          ...innerAcc,
          ...item,
        };
      }, acc);
    }

    if (typeof sxValue === 'object') {
      return {
        ...acc,
        ...sxValue,
      };
    }

    return acc;
  }, {});

  return Object.keys(merged).length ? merged : undefined;
};

/**
 * Render the review card
 */
function ReviewsCard({
  total,
  rating,
  histogram,
  slots,
  sx,
  ...rest
}: ReviewCardProps): JSX.Element {
  const id = useId();

  // the histogram is an array of the number of reviews for each star rating
  // we need to reverse it so that the 5 star rating is at the top
  const reversedHistogram = histogram.slice().reverse();

  return (
    <Flex
      paddingX={[6, 60]}
      paddingY={[8, 60]}
      flexDirection="column"
      background="tints.lightCream"
      borderRadius={12}
      gap={4}
      // desktop width from the design
      width={['100%', '551px']}
      {...rest}
      sx={mergeSlotSx(sx, slots?.root?.sx)}
    >
      <Flex gap={4} alignItems="center" sx={slots?.header?.sx}>
        <Icon
          as={Star}
          color={'utility.rating'}
          fontSize={'24px'}
          weight="fill"
          sx={slots?.icon?.sx}
        />
        <Heading
          size="md"
          color="tints.black"
          fontWeight="medium"
          sx={slots?.heading?.sx}
        >
          <strong>{rating.toFixed(1)}</strong>{' '}
          <Text
            as="span"
            size="lg"
            color="tints.darkGrey"
            sx={slots?.ratingLabel?.sx}
          >
            out of
          </Text>{' '}
          <strong>5</strong>
        </Heading>
      </Flex>
      <Flex
        as="ul"
        flexDirection="column"
        gap={2}
        sx={slots?.histogram?.sx}
      >
        {reversedHistogram.map((value, index) => {
          const percentage = Math.round((value / total) * 100);
          const label = `${5 - index} star`;

          return (
            <ReviewBar
              key={`${id}-${index}`}
              label={label}
              color={
                index === 0 ? 'colors.utility.rating' : 'colors.tints.uiGrey'
              }
              percentage={percentage}
              slots={slots}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}

export default ReviewsCard;

export type ReviewBarProps = {
  label: string;
  color: string;
  percentage: number;
  slots?: ReviewsCardSlotOverrides;
};

function ReviewBar(props: ReviewBarProps) {
  return (
    <Flex
      as="li"
      gap={2}
      alignItems="center"
      sx={props.slots?.histogramItem?.sx}
    >
      <Text
        size="md"
        color="tints.darkGrey"
        minWidth={34}
        sx={props.slots?.histogramLabel?.sx}
      >
        {props.label}
      </Text>
      <Box flex={1} sx={props.slots?.histogramBarContainer?.sx}>
        <Progress
          size="md"
          value={props.percentage}
          background="colors.tints.white"
          filledBackground={props.color}
          aria-label={`${props.percentage.toFixed(0)}% of the reviews are ${props.label
            }`}
          sx={props.slots?.histogramBar?.sx}
        />
      </Box>

      <Text
        size="md"
        color="tints.darkGrey"
        sx={props.slots?.histogramPercentage?.sx}
      >
        {props.percentage.toFixed(0)}%
      </Text>
    </Flex>
  );
}
